import React, { useEffect, useState } from "react";
import apiClient from "../../api/client";
import { toast } from "react-toastify";
import OperatorSearchSelect from "../OperatorSearchSelect";
import { getErrorMessage } from "../../utils/network";
import "./DvplSyncCustomerModal.css";

/**
 * "Sync customer" on the DVPL page: copies one customer from the OLD RADIUS
 * to DVPL, given the old customer id.
 *
 *  1. Look up  -> POST action:"preview" (nothing is written)
 *  2. Review   -> name, expiry, old package -> new package, partner/branch
 *  3. Sync     -> POST action:"migrate"
 *
 * Partner id, branch id and the package mapping are decided by the server
 * (operator_details + the fixed map in dvpl_sync_customer.php); the browser
 * only sends the old customer id (and, for a superadmin, which operator).
 */
const URL = "/customer/dvpl_sync_customer.php";

const DvplSyncCustomerModal = ({ visible, onClose, role, onSynced }) => {
    const isSuper = role === "superadmin";

    const [oldId, setOldId] = useState("");
    const [operatorId, setOperatorId] = useState("");
    const [operators, setOperators] = useState([]);
    const [preview, setPreview] = useState(null);
    const [done, setDone] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    // reset every time it is opened
    useEffect(() => {
        if (!visible) return;
        setOldId("");
        setOperatorId("");
        setPreview(null);
        setDone(null);
        setError("");
        setBusy(false);
    }, [visible]);

    // superadmin picks which operator / admin the customer is created under
    useEffect(() => {
        if (!visible || !isSuper) return;
        let cancelled = false;
        apiClient
            .get("/operator_api.php")
            .then(({ data }) => {
                if (!cancelled) setOperators(data?.operators || []);
            })
            .catch(() => {
                if (!cancelled) toast.error("Failed to load operators");
            });
        return () => {
            cancelled = true;
        };
    }, [visible, isSuper]);

    if (!visible) return null;

    const idValid = /^\d{1,12}$/.test(oldId.trim());
    const ready = idValid && (!isSuper || !!operatorId);

    const send = async (action) => {
        const { data } = await apiClient.post(URL, {
            action,
            old_customer_id: oldId.trim(),
            ...(isSuper ? { operator_id: Number(operatorId) } : {}),
        });
        if (!data.success) throw new Error(data.message || "Request failed");
        return data;
    };

    const lookUp = async () => {
        if (!ready || busy) return;
        setBusy(true);
        setError("");
        setPreview(null);
        try {
            const data = await send("preview");
            setPreview(data.preview);
        } catch (err) {
            setError(getErrorMessage(err, "Could not look up the customer"));
        } finally {
            setBusy(false);
        }
    };

    const sync = async () => {
        if (!preview || busy) return;
        setBusy(true);
        setError("");
        try {
            const data = await send("migrate");
            setDone(data);
            toast.success("Customer synced to DVPL");
            if (onSynced) onSynced();
        } catch (err) {
            setError(getErrorMessage(err, "Sync failed"));
        } finally {
            setBusy(false);
        }
    };

    const changeId = (value) => {
        setOldId(value.replace(/\D/g, "").slice(0, 12));
        setPreview(null);
        setError("");
    };

    const row = (label, value) => (
        <div className="dvs-row">
            <span>{label}</span>
            <strong>{value === "" || value == null ? "—" : String(value)}</strong>
        </div>
    );

    return (
        <div className="dvs-overlay" onClick={busy ? undefined : onClose}>
            <div
                className="dvs-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Sync customer to DVPL"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="dvs-head">
                    <h3>Sync customer to DVPL</h3>
                    <button type="button" className="dvs-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
                </div>

                {done ? (
                    <>
                        <div className="dvs-body">
                            <div className="dvs-note dvs-note--ok">
                                <strong>{done.preview?.name || done.username}</strong> was created in DVPL.
                            </div>
                            {Array.isArray(done.skipped_documents) && done.skipped_documents.length > 0 && (
                                <div className="dvs-note dvs-note--warn">
                                    These documents could not be copied and were left out:{" "}
                                    <strong>{done.skipped_documents.map((d) => String(d.field).replace(/_/g, " ")).join(", ")}</strong>.
                                    Add them to the customer manually.
                                </div>
                            )}
                            {row("Username", done.username)}
                            {row("DVPL reference", done.ref_id || "N/A")}
                            {row("Valid till", done.preview?.expiry)}
                            {row("Plan", `Package ${done.preview?.new_package_id} / Sub-plan ${done.preview?.new_sub_plan_id}`)}
                            <p className="dvs-hint">The customer now appears in this list. Open them to add them to the portal.</p>
                        </div>
                        <div className="dvs-foot">
                            <button type="button" className="dvs-btn dvs-btn--primary" onClick={onClose}>Done</button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="dvs-body">
                            {isSuper && (
                                <div className="dvs-field">
                                    <label>Create under</label>
                                    <OperatorSearchSelect
                                        placeholder="-- Select Operator/Admin --"
                                        value={operatorId}
                                        options={operators}
                                        onChange={(id) => {
                                            setOperatorId(id == null ? "" : id);
                                            setPreview(null);
                                            setError("");
                                        }}
                                    />
                                    <span className="dvs-hint">Their DVPL partner and branch are used.</span>
                                </div>
                            )}

                            <div className="dvs-field">
                                <label htmlFor="dvs-old-id">Old customer ID</label>
                                <div className="dvs-inline">
                                    <input
                                        id="dvs-old-id"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder="e.g. 9481"
                                        value={oldId}
                                        onChange={(e) => changeId(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") lookUp(); }}
                                    />
                                    <button type="button" className="dvs-btn" onClick={lookUp} disabled={!ready || busy}>
                                        {busy && !preview ? "Looking up…" : "Look up"}
                                    </button>
                                </div>
                            </div>

                            {error && <div className="dvs-note dvs-note--err" role="alert">{error}</div>}

                            {preview && (
                                <div className="dvs-card">
                                    {preview.already_migrated && (
                                        <div className="dvs-note dvs-note--warn">
                                            Already synced on {preview.already_migrated.at}
                                            {preview.already_migrated.ref_id ? ` (ref ${preview.already_migrated.ref_id})` : ""}.
                                        </div>
                                    )}
                                    {row("Name", preview.name)}
                                    {row("Username", preview.username)}
                                    {row("Mobile", preview.mobile)}
                                    {row("Status", preview.status)}
                                    {row("Valid till", preview.expiry)}
                                    {row("Old plan", `${preview.old_package || "—"}${preview.old_sub_plan ? " · " + preview.old_sub_plan : ""}`)}
                                    {row("New plan", `Package ${preview.new_package_id} / Sub-plan ${preview.new_sub_plan_id}`)}
                                    {row("Partner / Branch", `${preview.partner_id} / ${preview.branch_id}`)}
                                    {row("Created under", preview.created_under)}
                                </div>
                            )}
                        </div>
                        <div className="dvs-foot">
                            <button type="button" className="dvs-btn" onClick={onClose} disabled={busy}>Cancel</button>
                            <button
                                type="button"
                                className="dvs-btn dvs-btn--primary"
                                onClick={sync}
                                disabled={!preview || busy || !!preview?.already_migrated}
                            >
                                {busy && preview ? "Syncing…" : "Sync customer"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DvplSyncCustomerModal;
